import React from 'react';
import { METHOD_COLORS } from '../../lib/gis-types';
import { CRS_OPTIONS } from '../../lib/coordinates';
import { captureMapDataUrl } from '../../lib/export';
import AddServiceDialog from '../AddServiceDialog';
import ModelBuilder from '../ModelBuilder';
import { viewExtentOf } from '../../lib/ribbon-analysis';
import MapLayout from '../MapLayout';
import ReportBuilder from '../ReportBuilder';
import { reportTitle, reportTableRows } from '../../lib/report-layout';
import GeoreferencingTool from '../GeoreferencingTool';
import FileImportDialog from '../FileImportDialog';

export default function MapDialogs({ datasetContext = null, demoEnabled = false, showModelBuilder, setShowModelBuilder, showMapLayout, setShowMapLayout, showGeoreferencing, setShowGeoreferencing, showReportBuilder, setShowReportBuilder, reportType = null, setReportType, projectCenter, currentProject, layers, importedLayers, resultLayers, setResultLayers, chartableLayers, lineData, activeXyzLayer, drawnFeatures, showFileImport, setShowFileImport, showServiceDialog, setShowServiceDialog, handleImport, addRasterLayer, addVectorLayer, mapRefHolder, coordSys }) {
  // The project the report belongs to: the open project, else the one the ?dataset= belongs to (V.52)
  const reportProject = currentProject || datasetContext?.project || null;
  return (
    <>
      {/* Add Service Dialog */}
      {showServiceDialog && (
        <AddServiceDialog
          initialType={typeof showServiceDialog === 'string' ? showServiceDialog : 'xyz'}
          onAddRasterLayer={addRasterLayer}
          onAddVectorLayer={addVectorLayer}
          getBbox={() => { const b = mapRefHolder?.current?.current?.getMap()?.getBounds(); return b ? [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()] : null; }}
          onClose={() => setShowServiceDialog(false)}
        />
      )}

      {/* Model Builder (full-screen overlay) */}
      {showModelBuilder && (
        <ModelBuilder
          layers={[
            ...chartableLayers,
            ...(drawnFeatures.length > 0 ? [{ id: 'drawn', name: 'Drawn Features', geojson: { type: 'FeatureCollection', features: drawnFeatures } }] : []),
          ]}
          onAddResultLayer={(geojson, name) => {
            setResultLayers(prev => [...prev, { id: `result-${Date.now()}-${prev.length}`, name, geojson, visible: true }]);
          }}
          getViewExtent={() => viewExtentOf(mapRefHolder)}
          onClose={() => setShowModelBuilder(false)}
        />
      )}

      {/* Map Layout / Print (full-screen overlay) */}
      {showMapLayout && (
        <MapLayout
          mapRefHolder={mapRefHolder}
          layers={[
            ...layers.filter(l => l.visible).map(l => ({ id: l.id, name: l.name, visible: true, color: METHOD_COLORS[l.method] || '#8b949e' })),
            ...importedLayers.filter(l => l.visible).map(l => ({ id: l.id, name: l.name, visible: true, color: '#a78bfa' })),
            ...resultLayers.filter(r => r.visible && !(r.kind === 'grid' || r.kind === 'contour')).map(r => ({ id: r.id, name: r.name, visible: true, color: '#00d4ff' })),
          ]}
          colorScales={resultLayers.filter(r => r.visible && (r.kind === 'grid' || r.kind === 'contour') && r.geojson?.metadata).map(r => ({
            title: r.name, min: r.geojson.metadata.min, max: r.geojson.metadata.max,
            colors: r.geojson.metadata.colors || ['#2563eb', '#00b8de', '#34d399', '#fbbf24', '#f87171'],
          }))}
          projectName={currentProject?.name || 'Ultimate-GIS Project'}
          crsLabel={CRS_OPTIONS.find(c => c.code === coordSys)?.label || ''}
          onSendToReport={() => { setShowMapLayout(false); setReportType?.(null); setShowReportBuilder(true); }}
          onClose={() => setShowMapLayout(false)}
        />
      )}

      {/* Report Builder (full-screen overlay) */}
      {showReportBuilder && (
        <ReportBuilder
          reportType={reportType}
          project={reportProject}
          resultLayers={resultLayers}
          getMapSnapshot={async () => {
            try {
              const map = mapRefHolder.current?.getMap?.() || mapRefHolder.current?.current?.getMap?.();
              return await captureMapDataUrl(map);
            } catch { return null; }
          }}
          legendLayers={[
            ...layers.filter(l => l.visible).map(l => ({ name: l.name, color: METHOD_COLORS[l.method] || '#8b949e' })),
            ...importedLayers.filter(l => l.visible).map(l => ({ name: l.name, color: '#a78bfa' })),
            ...resultLayers.filter(r => r.visible).map(r => ({ name: r.name, color: '#00d4ff' })),
          ]}
          tableRows={reportTableRows([[...(activeXyzLayer ? [activeXyzLayer] : []), ...importedLayers], resultLayers.filter(r => !(r.kind === 'grid' || r.kind === 'contour'))], lineData.features)}
          projectName={reportTitle({ project: reportProject, datasetName: datasetContext?.datasetName, demoEnabled })}
          projectId={reportProject?.id}
          organizationId={reportProject?.organization_id}
          onClose={() => { setShowReportBuilder(false); setReportType?.(null); }}
        />
      )}

      {/* Georeferencing Tool (full-screen overlay) */}
      {showGeoreferencing && (
        <GeoreferencingTool
          initialCenter={projectCenter || { lat: 41.99, lon: -70.15 }}
          onClose={() => setShowGeoreferencing(false)}
        />
      )}

      {/* File Import Dialog */}
      {showFileImport && (
        <FileImportDialog
          onImport={handleImport}
          onClose={() => setShowFileImport(false)}
        />
      )}
    </>
  );
}
